import React, { useEffect, useMemo, useRef, useState } from 'react';
import KPITile from '../../../../../common/KPITile/KPITile';
import KPIDetailTile from '../../../../../common/KPIDetailTile/KPIDetailTile';
import VarianceValue from '../../../../../common/VarianceValue/VarianceValue';
import styles from './CorePlusIncomeTile.module.scss';
import { useTranslation } from 'react-i18next';
import ViewModeSwitcher, {
  ViewMode,
} from '../../../../../common/ViewModeSwitcher/ViewModeSwitcher';
import GenericTable from '../../../../../common/GenericTable/GenericTable';
import InsightAI from '../../../../../common/InsightAI/InsightAI';
import StackedBarChart from '../../../../../common/StackedBarChart/StackedBarChart';
import isEmpty from 'lodash/isEmpty';
import { useGetAGCAIDetailsMutation } from '../../../../../../redux/services/agcAIAPI';
import { useGetBusinessPerformanceDetailsQuery } from '../../../../../../redux/services/businessPerformaceAPI';
import { useProfileParams, isAISummariesAccessible } from '../../../../../../utils/common';
import Skeleton from '../../../../../common/Skeleton/Skeleton';
import { useAppSelector } from '../../../../../../redux/hooks';
import { useGetOptionsListQuery } from '../../../../../../redux/services/optionMasterAPI';
import { getFilteredDynamicLinkList } from '../../../../../../utils/externalNavigationLinkUtil';
import { countryMappings } from '../../../../../../constants/dataConstants';
import EditIconLight from '../../../../../../assets/images/edit_link_light.svg';
import EditIconDark from '../../../../../../assets/images/edit_link_dark.svg';
import { sanitizeStackedBarData } from '../../../../../../utils/stackedBarChartDataSanitizer';
import { CurrencyInputFormatter } from '../../../../../../utils/currencyFormatter';

interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

const CorePlusIncomeTile: React.FC<SummaryProp> = ({ summary, loading }) => {
  const { t } = useTranslation();
  const monthlyNum = typeof summary?.kpiValue === 'number' ? summary?.kpiValue : undefined;
  const totalNum =
    typeof summary?.metricSourceData?.[1]?.value === 'number'
      ? summary?.metricSourceData?.[1]?.value
      : undefined;
  const annualNum =
    typeof summary?.metricSourceData?.[0]?.value === 'number'
      ? summary?.metricSourceData?.[0]?.value
      : undefined;

  return (
    <KPITile title={t('corePlusIncome.title')} dataTestId="core-plus-income-tile" loading={loading}>
      <div className={styles.tile_content}>
        <div className={styles.value_wrapper}>
          <div className={styles.wrapper_col}>
            <div className={styles?.sub_title}>{t('corePlanIncome.subtitle_monthlyYTDTotal')}</div>
            <div className={styles.value_content_subsection}>
              {CurrencyInputFormatter(summary?.kpiCurrency, monthlyNum, true)}
            </div>
            <VarianceValue
              value={summary?.kpiVarianceValue ?? 0}
              dataType={summary?.kpiVarianceDataType}
              currency={summary?.kpiCurrency}
              comparisonCriteria={summary?.kpiVarianceComparisonCriteria}
            />
          </div>

          <div className={styles.wrapper_col}>
            <div className={styles?.sub_title}>{t('corePlanIncome.subtitle_total')}</div>
            <div className={styles.value_content}>
              {CurrencyInputFormatter(summary?.kpiCurrency, totalNum, true)}
            </div>
            <VarianceValue
              value={summary?.metricSourceData?.[1]?.varianceValue ?? 0}
              dataType={summary?.metricSourceData?.[1]?.varianceDataType}
              currency={summary?.metricSourceData?.[1]?.currency}
              comparisonCriteria={summary?.metricSourceData?.[1]?.varianceCurrency}
            />
          </div>
        </div>
        <div>
          <div className={styles?.sub_title}>{t('corePlanIncome.subtitle_annual')}</div>
          <div className={styles.value_content_subsection}>
            {CurrencyInputFormatter(summary?.kpiCurrency, annualNum, true)}
          </div>
        </div>
      </div>
    </KPITile>
  );
};

export default CorePlusIncomeTile;

// ---------------- Views ---------------- //
interface CorePlusMonthlyViewProps {
  viewMode: ViewMode;
  tableData: any;
  stackedBarChartData?: any;
  kpiCode: string;
  yAxisValues?: number[];
  currency?: string;
}

const CorePlusMonthlyView: React.FC<CorePlusMonthlyViewProps> = ({
  viewMode,
  tableData,
  stackedBarChartData,
  kpiCode,
  yAxisValues,
  currency,
}) => {
  const { t } = useTranslation();
  if (viewMode === 'left') {
    return isEmpty(stackedBarChartData) ? (
      <div className={styles.no_data}>{t('noDataAvailable')}</div>
    ) : (
      <StackedBarChart data={stackedBarChartData} currency={currency} yAxisValues={yAxisValues} />
    );
  }

  return isEmpty(tableData) ? (
    <div className={styles.no_data}>{t('noDataAvailable')}</div>
  ) : (
    <GenericTable tableData={tableData} kpiCode={kpiCode} currency={currency} />
  );
};

const CorePlusYearlyView: React.FC<CorePlusMonthlyViewProps> = ({
  viewMode,
  tableData,
  stackedBarChartData,
  kpiCode,
  yAxisValues,
  currency,
}) => {
  const { t } = useTranslation();
  if (viewMode === 'left') {
    return isEmpty(stackedBarChartData) ? (
      <div className={styles.no_data}>{t('noDataAvailable')}</div>
    ) : (
      <StackedBarChart data={stackedBarChartData} currency={currency} yAxisValues={yAxisValues} />
    );
  }

  return isEmpty(tableData) ? (
    <div className={styles.no_data}>{t('noDataAvailable')}</div>
  ) : (
    <GenericTable tableData={tableData} kpiCode={kpiCode} currency={currency} />
  );
};

export const CorePlusIncomeDetail: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('left');
  const [periodCode, setPeriodCode] = useState<string>('monthly');
  const kpiCode = 'corePlusIncome';
  const { affiliateCode, aboNumber } = useProfileParams();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const editIcon = currentTheme === 'dark' ? EditIconDark : EditIconLight;

  const userDetails = useAppSelector((state: any) => state.userDetails.user);
  const selectedLang = useAppSelector((state: any) => state.language.currentLanguage);

  const { data: optionsData } = useGetOptionsListQuery({
    applicationCode: 'amw',
    optionMasterName: 'siteheader-externalsite',
  });

  const myBizLink = useMemo(() => {
    if (!optionsData?.body?.options || !userDetails?.affiliateCode) return null;
    const links = getFilteredDynamicLinkList(optionsData.body.options, userDetails.affiliateCode);
    return links?.find((l: any) => l.value === 'My Biz' || l.value === 'MyBiz');
  }, [optionsData, userDetails]);

  const appendUTMParams = (url: any, utmParams: any) => {
    const urlObj = new URL(url);
    const params = new URLSearchParams(utmParams);
    params.forEach((value, key) => {
      urlObj.searchParams.append(key, value);
    });
    return urlObj.toString();
  };

  const handleMyBizClick = () => {
    if (!myBizLink) return;
    const clickedSiteLinkObj = myBizLink;
    let siteLink = '';

    if (clickedSiteLinkObj?.hasParameter) {
      siteLink = clickedSiteLinkObj?.code
        ?.replace('{affiliateCode}', userDetails.affiliateCode)
        ?.replace('{aboNumber}', userDetails.aboNumber)
        ?.replace('{aboNumberBase64}', window.btoa(userDetails.aboNumber))
        ?.replace('{locale}', i18n.language);
    } else {
      siteLink = clickedSiteLinkObj?.code ?? '';
    }

    const userNativeId = localStorage.getItem('auth-nativeid');
    const utmCampaign = `${countryMappings[userDetails.userAffiliateCode]}_${selectedLang}_${userNativeId}`;
    const utmTrackingCode = `?utm_source=am_workspace&utm_medium=linkout&utm_campaign=${utmCampaign}`;
    const finalURL =
      clickedSiteLinkObj?.systemCode === 'boss'
        ? siteLink
        : appendUTMParams(siteLink, utmTrackingCode);

    window.open(finalURL, '_blank');
  };

  const { data: detailsResponse, isLoading } = useGetBusinessPerformanceDetailsQuery(
    {
      affiliateCode,
      aboNumber,
      payload: { kpis: [{ code: kpiCode }] } as any,
    },
    { skip: !onClose }
  );

  const [getAGCAIDetails, { data: aiDetailsResp, isLoading: isLoadingAiSummary }] =
    useGetAGCAIDetailsMutation();
  const hasRequestedRef = useRef(false);
  useEffect(() => {
    const canFetch = Number(affiliateCode) !== 0 && aboNumber !== 0 && Boolean(onClose);
    if (!canFetch || hasRequestedRef.current) return;
    hasRequestedRef.current = true;
    getAGCAIDetails({
      affiliateCode,
      aboNumber,
      payload: { kpis: [{ code: kpiCode }] } as any,
    });
  }, [affiliateCode, aboNumber, onClose, kpiCode, getAGCAIDetails]);

  const aiRaw = aiDetailsResp?.body;
  const aiResolved = typeof aiRaw === 'string' ? aiRaw : aiRaw?.[kpiCode] || aiRaw?.summary || '';
  const baseAI = aiResolved ? String(aiResolved).slice(0, 300).concat('...') : '';
  const aiContent = baseAI
    ? `${periodCode === 'monthly' ? '[Monthly] ' : '[Yearly] '}${baseAI}`
    : '';

  const detailData = detailsResponse?.body?.kpis?.[0] || {};
  const kpiCurrency = detailsResponse?.body?.kpis?.[0]?.kpiCurrency ?? '';

  // Extract monthly view data
  const monthlyCharts = detailData?.detail?.monthlyView?.chart || [];
  const monthlyBarChart = monthlyCharts.find(
    (chart: any) => chart?.chartType === 'barChart' || chart?.chartType === 'stackedBarChart'
  );
  const monthlyTableChart = monthlyCharts.find((chart: any) => chart?.chartType === 'table');
  const monthlyBarChartDetail =
    monthlyBarChart?.chartDetail?.barChartDetail ||
    monthlyBarChart?.chartDetail?.stackedBarChartDetail;
  const monthlyTableData = monthlyTableChart?.chartDetail?.tableDetail;

  // Extract annually view data
  const annuallyCharts = detailData?.detail?.anuallyView?.chart || [];
  const annuallyBarChart = annuallyCharts.find(
    (chart: any) => chart?.chartType === 'barChart' || chart?.chartType === 'stackedBarChart'
  );
  const annuallyTableChart = annuallyCharts.find((chart: any) => chart?.chartType === 'table');
  const annuallyBarChartDetail =
    annuallyBarChart?.chartDetail?.barChartDetail ||
    annuallyBarChart?.chartDetail?.stackedBarChartDetail;
  const annuallyTableData = annuallyTableChart?.chartDetail?.tableDetail;

  const monthlyYAxisValues = useMemo(() => {
    if (!monthlyBarChartDetail?.yAxis || !Array.isArray(monthlyBarChartDetail.yAxis))
      return undefined;
    return monthlyBarChartDetail.yAxis.map((item: any) => Number(item.dataValue));
  }, [monthlyBarChartDetail]);

  const annuallyYAxisValues = useMemo(() => {
    if (!annuallyBarChartDetail?.yAxis || !Array.isArray(annuallyBarChartDetail.yAxis))
      return undefined;
    return annuallyBarChartDetail.yAxis.map((item: any) => Number(item.dataValue));
  }, [annuallyBarChartDetail]);

  const monthlyStackedBarChartData = useMemo(
    () => sanitizeStackedBarData(monthlyBarChartDetail),
    [monthlyBarChartDetail]
  );
  const annuallyStackedBarChartData = useMemo(
    () => sanitizeStackedBarData(annuallyBarChartDetail),
    [annuallyBarChartDetail]
  );

  const tableData = periodCode === 'monthly' ? monthlyTableData : annuallyTableData;
  const stackedBarChartData =
    periodCode === 'monthly' ? monthlyStackedBarChartData : annuallyStackedBarChartData;
  const yAxisValues = periodCode === 'monthly' ? monthlyYAxisValues : annuallyYAxisValues;
  const hasChartData = !isEmpty(stackedBarChartData);

  useEffect(() => {
    if (!hasChartData) {
      setViewMode('right');
    }
  }, [hasChartData]);

  const tabs = useMemo(
    () => [
      { id: 'monthly', label: t('corePlusIncome.monthly_core_plus_bonus') },
      { id: 'yearly', label: t('corePlusIncome.annual_core_plus_bonus') },
    ],
    [t]
  );

  if (isLoading) return <Skeleton height="400px" />;

  const headerContent = (
    <div className={styles.header_content}>
      <div className={styles.tile_header}>
        <div className={styles.title}>{t('corePlusIncome.title_short')}</div>
        <div className={styles.desc}>{t('corePlusIncome.description')}</div>
      </div>
      <div className={styles.tab_wrapper}>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`${styles.tab_name} ${periodCode === tab.id ? styles.selected : ''}`}
            onClick={() => setPeriodCode(tab.id)}
          >
            {tab.label}
          </div>
        ))}
      </div>
      <div className={styles.controls_wrapper}>
        <div className={styles.switcher_container}>
          <ViewModeSwitcher mode={viewMode} onChangeMode={setViewMode} disabled={false} />
        </div>
        {periodCode === 'monthly' && (
          <div className={styles.external_link} onClick={handleMyBizClick}>
            <span>{t('corePlusIncome.myBiz')}</span>
            <img src={editIcon} alt="link" />
          </div>
        )}
      </div>
    </div>
  );

  const leftContent =
    periodCode === 'monthly' ? (
      <CorePlusMonthlyView
        viewMode={viewMode}
        tableData={tableData}
        stackedBarChartData={stackedBarChartData}
        kpiCode={kpiCode}
        yAxisValues={yAxisValues}
        currency={kpiCurrency}
      />
    ) : (
      <CorePlusYearlyView
        viewMode={viewMode}
        tableData={tableData}
        stackedBarChartData={stackedBarChartData}
        kpiCode={kpiCode}
        yAxisValues={yAxisValues}
        currency={kpiCurrency}
      />
    );

  const rightContent = isAISummariesAccessible() ? (
    isLoadingAiSummary ? (
      <Skeleton height="200px" />
    ) : (
      <InsightAI
        updateDate=""
        content={aiContent}
        title={t('ai_summary')}
        showDropdown={false}
        isDropdownEnabled={true}
        flowerIconSize={20}
        infoIconSize={12}
        titleSize={14}
      />
    )
  ) : null;

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="core-plus-income-detail"
    />
  );
};
